import { gql } from '@apollo/client';
import { reviewIntegrationFragments } from './hospitals';

export const fragments = {
  base: gql`
    fragment PhysicianBase on Physician {
      id
      hstId
      name
      email
    }
  `,
  all: gql`
    fragment PhysicianData on Physician {
      id
      hstId
      name
      email
      speciality
      category
      notificationNumbers
      hasScheduleAccess
      scheduleAccessAll
      shouldSendNotification
      excludeFromPreopRegistration
      isAdvancedPracticeProvider
      reviewIntegrations {
        google {
          ...ReviewBase
        }
        yelp {
          ...ReviewBase
        }
      }
    }
    ${reviewIntegrationFragments.base}
  `,
};

export const list = gql`
  query physicians {
    physicians {
      ...PhysicianData
      pin
      bracelet {
        id
        batteryLevel
        updatedAt
      }
      room {
        id
        name
      }
    }
  }
  ${fragments.all}
`;

export const subscription = gql`
  subscription physicians($filter: Filter!) {
    physicians(filter: $filter) {
      physician {
        ...PhysicianData
      }
      stat {
        bookedSlots
        blockUtilization
        relativeUtilization
        bookedHours
        utilizedHours
        actualUtilizedHours
        actualUtilization
        bookedPercentage
      }
    }
  }
  ${fragments.all}
`;

export const listForProcedureType = gql`
  query physiciansForProcedureType($procedureType: Long) {
    physicians: physiciansForProcedureType(procedureType: $procedureType) {
      ...PhysicianData
    }
  }
  ${fragments.all}
`;

export const item = gql`
  query physician($id: Long!) {
    physician(id: $id) {
      ...PhysicianData
      pin
      procedures {
        id
        name
      }
      bracelet {
        id
        batteryLevel
        updatedAt
      }
      room {
        id
        name
      }
    }
  }
  ${fragments.all}
`;

export const create = gql`
  mutation createPhysician(
    $name: String!
    $braceletId: String
    $hstId: HstId
    $email: String
    $notificationNumbers: String
    $shouldSendNotification: Boolean
    $excludeFromPreopRegistration: Boolean
    $isAdvancedPracticeProvider: Boolean
    $speciality: String
    $category: ProcedureCategory
    $reviewIntegrations: ReviewIntegrationsInput
  ) {
    createPhysician(
      name: $name
      braceletId: $braceletId
      hstId: $hstId
      email: $email
      notificationNumbers: $notificationNumbers
      shouldSendNotification: $shouldSendNotification
      excludeFromPreopRegistration: $excludeFromPreopRegistration
      isAdvancedPracticeProvider: $isAdvancedPracticeProvider
      speciality: $speciality
      category: $category
      reviewIntegrations: $reviewIntegrations
    ) {
      ...PhysicianData
    }
  }
  ${fragments.all}
`;

export const update = gql`
  mutation editPhysician(
    $id: Long!
    $braceletId: String
    $hstId: HstId
    $name: String!
    $email: String
    $notificationNumbers: String
    $shouldSendNotification: Boolean
    $excludeFromPreopRegistration: Boolean
    $isAdvancedPracticeProvider: Boolean
    $speciality: String
    $category: ProcedureCategory
    $reviewIntegrations: ReviewIntegrationsInput
  ) {
    editPhysician(
      id: $id
      braceletId: $braceletId
      hstId: $hstId
      name: $name
      email: $email
      notificationNumbers: $notificationNumbers
      shouldSendNotification: $shouldSendNotification
      excludeFromPreopRegistration: $excludeFromPreopRegistration
      isAdvancedPracticeProvider: $isAdvancedPracticeProvider
      speciality: $speciality
      category: $category
      reviewIntegrations: $reviewIntegrations
    ) {
      ...PhysicianData
    }
  }
  ${fragments.all}
`;

export const remove = gql`
  mutation deletePhysician($id: Long!) {
    deletePhysician(id: $id)
  }
`;

export const removeAndAssign = gql`
  mutation deletePhysicianAndAssign($id: Long!, $assignTo: Long!) {
    deletePhysician(id: $id, assignTo: $assignTo)
  }
`;

export const applyPhysicianProcedureTypeChanges = gql`
  mutation applyPhysicianProcedureTypeChanges(
    $physician: Long!
    $assignProcedures: [Long!]!
    $unassignProcedures: [Long!]!
  ) {
    applyPhysicianProcedureTypeChanges(
      physician: $physician
      assignProcedures: $assignProcedures
      unassignProcedures: $unassignProcedures
    )
  }
`;

export const grantScheduleAccess = gql`
  mutation grantScheduleAccessToPhysician($id: Long!) {
    grantScheduleAccessToPhysician(id: $id) {
      ...PhysicianData
    }
  }
  ${fragments.all}
`;

export const resendScheduleAccessLink = gql`
  mutation resendScheduleAccessLinkToPhysician($id: Long!) {
    resendScheduleAccessLinkToPhysician(id: $id) {
      ...PhysicianData
    }
  }
  ${fragments.all}
`;

export const setScheduleAccessAllPhysician = gql`
  mutation setScheduleAccessAllPhysician($id: Long!, $scheduleAccessAll: Boolean!) {
    setScheduleAccessAllPhysician(id: $id, scheduleAccessAll: $scheduleAccessAll)
  }
`;

export const getScheduleAccessAllPhysician = gql`
  query getScheduleAccessAllPhysician($id: Long) {
    getScheduleAccessAllPhysician(id: $id)
  }
`;

export const revokeScheduleAccess = gql`
  mutation revokeScheduleAccessToPhysician($id: Long!) {
    revokeScheduleAccessToPhysician(id: $id) {
      ...PhysicianData
    }
  }
  ${fragments.all}
`;

// ============================================================================
// PAGINATED QUERIES - Server-side pagination for optimal performance
// ============================================================================

/**
 * Paginated physicians query.
 * Uses SQL LIMIT/OFFSET for efficient server-side pagination and supports
 * server-side filtering by passing flat variables that are mapped into the
 * shared Filter model on the server (name, speciality, category, etc.).
 */
export const physiciansPaginatedQuery = gql`
  query physicians(
    $name: String
    $speciality: String
    $category: ProcedureCategory
    $offset: Int
    $limit: Int
    $sortBy: String
    $sortOrder: String
  ) {
    physicians(
      filter: { name: $name, speciality: $speciality, category: $category }
      offset: $offset
      limit: $limit
      sortBy: $sortBy
      order: $sortOrder
    ) {
      ...PhysicianData
      pin
      bracelet {
        id
        batteryLevel
        updatedAt
      }
      room {
        id
        name
      }
    }
  }
  ${fragments.all}
`;

/**
 * Count query for physicians.
 * Returns total count matching the current filter for pagination controls.
 */
export const physiciansCountQuery = gql`
  query physiciansCount($filter: Filter) {
    physiciansCount(filter: $filter)
  }
`;

import { GraphScheme } from './Scheme';

const scheme: GraphScheme = {
  list,
  item,
  create,
  update,
  remove,
  applyPhysicianProcedureTypeChanges,
  grantScheduleAccess,
  resendScheduleAccessLink,
  revokeScheduleAccess,
  setScheduleAccessAllPhysician,
  // Paginated queries - uses same endpoint with optional parameters
  physiciansPaginatedQuery,
  physiciansCountQuery,
};

export default scheme;
